import { supabase } from "@/integrations/supabase/client";
import { pageTypeFor } from "@/lib/analytics";
import { LANDING_SLUGS } from "@/seo/landings";

// Medición de audiencia sin cookies (29-10-2026, portada de RE:PÚBLICO,
// src/lib/audience.ts + src/hooks/useAudienceCount.ts de republico-web).
// Cuenta vistas, tiempo (por tramos) y acciones POR PÁGINA Y DÍA en
// page_audience_daily / page_time_daily / page_action_daily, vía las RPC
// record_page_view / record_page_time / record_page_action (migración
// 20261029200000_page_audience.sql). Exenta de consentimiento según el art.
// 22.2 LSSI y la guía de la AEPD sobre medición de audiencia (ver /cookies y
// /privacidad). Reglas que NO hay que romper:
//   - Nada en el dispositivo: ni cookie, ni localStorage, ni sessionStorage.
//     La vista actual y la anterior viven solo en memoria de esta carga.
//   - Nada que identifique: ruta, tipo de página, ciudad de la página, tipo de
//     origen, movil/escritorio (por el ancho de la ventana, sin user-agent),
//     la acción y, para el tiempo, los segundos (la base guarda el tramo).
//   - Cuenta a todo el mundo, también a quien rechaza la cookie de PostHog: la
//     exención no depende del consentimiento. PostHog sigue igual, aparte.
//   - El equipo (admin, cuentas sandbox, personal de caja) no cuenta: lo
//     decide la base con la sesión de la llamada, sin guardarla.
//   - Un fallo nunca rompe la interfaz: todo va en try/catch y sin await.

export type AudienceSource =
  | "google" | "bing" | "duckduckgo" | "ecosia" | "brave" | "yahoo" | "qwant" | "startpage" | "yandex"
  | "ia" | "instagram" | "redes" | "newsletter" | "anuncios" | "compartido"
  | "directo" | "interno" | "otro"
  // 2026-10-30: la tarjeta RE:CINE de las fichas de cine de RE:PÚBLICO.
  | "republico";

// "republico": clic hacia la app de RE:PÚBLICO desde la confirmación de la lista de espera.
export type AudienceAction = "waitlist_cta" | "waitlist_signup" | "ticketing" | "calendar" | "share" | "republico";

type AudiencePageType = ReturnType<typeof pageTypeFor>["page_type"] | "landing";
type Device = "movil" | "escritorio";

type View = {
  path: string;
  pageType: AudiencePageType;
  city: string;
  source: AudienceSource;
  device: Device;
  start: number;
  timed: boolean;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Solo la web publicada: local y las vistas previas de Vercel escriben en la
// misma base y se colarían como visitas reales.
const PRODUCTION_HOST = /^(www\.)?recine\.es$/;

const BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|inspectiontool|preview|facebookexternalhit|embedly|whatsapp/i;

// Páginas que no cuentan (ni cambian la página anterior): cuenta, servicio,
// herramientas internas y la URL antigua por UUID, que redirige a la ficha.
const NOT_COUNTED = /^\/(login|auth|healthcheck|simulaciones|simulaciones-cines|_internal)(\/|$)|^\/peliculas\/[^/]+$/;

// La vista contada ahora mismo (null en páginas que no cuentan) y la anterior
// de esta carga (para «continued» y para saber si es la de entrada).
let current: View | null = null;
let previous: { path: string; source: AudienceSource } | null = null;
// Origen de la primera página de esta visita (solo en memoria): la lista de
// espera lo añade al `source` del alta si se llegó desde RE:PÚBLICO.
let arrival: AudienceSource | null = null;
export const arrivalSource = () => arrival;
// Token de la sesión, para que la base reconozca al equipo también en los
// envíos con keepalive (que no pasan por supabase-js). Solo en memoria.
let token: string | null = null;

const refreshToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token ?? null;
  } catch {
    /* sin sesión: cuenta como anónimo */
  }
};

// fetch con keepalive: el envío sale aunque la página pase a segundo plano o
// se cierre justo después (el tiempo y los clics a webs externas van así).
const post = (fn: string, body: Record<string, unknown>) => {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;
  try {
    void fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token ?? SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // La medición nunca debe romper la interfaz.
  }
};

/** Ruta normalizada igual que public.audience_norm_path: sin query/hash, en
 *  minúsculas, sin barra final; lo desconocido se junta en '/(otra)'. */
const normalizePath = (pathname: string, pageType: AudiencePageType): string => {
  const n = pathname.split("#")[0].split("?")[0].toLowerCase().replace(/\/+$/, "");
  if (pageType === "other") return "/(otra)";
  if (!n) return "/";
  if (pageType === "home") return "/(otra)";
  return n.length <= 200 && /^(\/[a-z0-9][a-z0-9-]*){1,3}$/.test(n) ? n : "/(otra)";
};

/** Tipo de página: el de pageTypeFor (el mismo de PostHog), más las landings
 *  por ciudad (/{ciudad}/hoy…), que allí salen como "other". */
const classify = (pathname: string): { pageType: AudiencePageType; city: string } => {
  const { page_type, city } = pageTypeFor(pathname);
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (page_type === "other" && parts.length === 2 && (LANDING_SLUGS as readonly string[]).includes(parts[1])) {
    const c = pageTypeFor(`/${parts[0]}`);
    if (c.page_type === "city") return { pageType: "landing", city: c.city ?? "" };
  }
  return { pageType: page_type, city: city ?? "" };
};

// Origen de la PRIMERA vista de la carga. Las siguientes son "interno".
const entrySource = (): AudienceSource => {
  try {
    const params = new URLSearchParams(window.location.search);
    const medium = params.get("utm_medium")?.toLowerCase() ?? "";
    if (/^(paid|paid_social|paidsocial|cpc|ppc|cpm|ads?)$/.test(medium)) return "anuncios";
    const utm = params.get("utm_source")?.toLowerCase() ?? "";
    // Enlace compartido con nuestro botón (?s=… o utm_source=share, el de la
    // lista de espera). Manda sobre el referente: WhatsApp no trae ninguno.
    const shared = params.get("s")?.toLowerCase() ?? "";
    if (utm === "share" || shared === "app" || shared === "web" || shared === "share") return "compartido";
    if (utm.includes("newsletter") || utm.includes("brevo") || utm === "email") return "newsletter";
    if (utm.includes("instagram")) return "instagram";
    if (utm === "republico") return "republico";
    const ref = (document.referrer ? new URL(document.referrer).hostname : "").replace(/^www\./, "");
    if (!ref) return "directo";
    if (/(^|\.)republico\.app$/.test(ref)) return "republico";
    if (ref === window.location.hostname.replace(/^www\./, "") || /(^|\.)recine\.es$/.test(ref)) return "interno";
    if (/(^|\.)google\./.test(ref) && !ref.startsWith("gemini.")) return "google";
    if (/(^|\.)bing\.com$/.test(ref)) return "bing";
    // Buscadores alternativos por su nombre de dominio (no el correo web).
    const engine = ref.match(/(?:^|\.)(duckduckgo|ecosia|brave|yahoo|qwant|startpage|yandex)\.[a-z.]+$/)?.[1] as AudienceSource | undefined;
    if (engine) return /^(mail|correo|webmail)\./.test(ref) ? "otro" : engine;
    if (/chatgpt|openai|perplexity|copilot|gemini|claude\.ai/.test(ref)) return "ia";
    if (/instagram/.test(ref)) return "instagram";
    if (/facebook|fb\.|threads|t\.co$|twitter|x\.com|tiktok|linkedin|whatsapp|telegram|bsky|reddit/.test(ref)) return "redes";
    return "otro";
  } catch {
    return "otro";
  }
};

// Por el ancho de la ventana, sin leer el user-agent.
const device = (): Device => (window.innerWidth < 768 ? "movil" : "escritorio");

/** Tiempo en la vista actual, una sola vez por vista (tope 1 h). */
const flushTime = () => {
  const v = current;
  if (!v || v.timed) return;
  v.timed = true;
  const seconds = Math.min(3600, Math.max(0, Math.round((Date.now() - v.start) / 1000)));
  post("record_page_time", {
    p_path: v.path,
    p_page_type: v.pageType,
    p_city: v.city,
    p_source: v.source,
    p_device: v.device,
    p_seconds: seconds,
  });
};

/** Nueva página (la llama useAudience en cada cambio de ruta). */
export const recordAudienceView = (pathname: string) => {
  try {
    if (typeof window === "undefined") return;
    const { pageType, city } = classify(pathname);
    const path = normalizePath(pathname, pageType);
    if (current && current.path === path) return;
    flushTime();
    if (NOT_COUNTED.test(pathname) || pageType === "login") {
      current = null;
      return;
    }
    if (!PRODUCTION_HOST.test(window.location.hostname)) return;
    if (navigator.webdriver || BOT_UA.test(navigator.userAgent)) return;

    const source: AudienceSource = previous ? "interno" : entrySource();
    if (!previous) arrival = source;
    const dev = device();
    current = { path, pageType, city, source, device: dev, start: Date.now(), timed: false };
    const from = previous;
    previous = { path, source };

    const send = async () => {
      // Con la sesión ya recuperada, para que la base reconozca al equipo
      // desde la primera página.
      await refreshToken();
      post("record_page_view", {
        p_path: path,
        p_page_type: pageType,
        p_city: city,
        p_source: source,
        p_device: dev,
        p_from: from && from.path !== path ? from.path : null,
        p_from_source: from && from.path !== path ? from.source : null,
      });
    };
    // Fuera del camino crítico de la primera pintura.
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    if (ric) ric(() => void send(), { timeout: 3000 });
    else window.setTimeout(() => void send(), 1500);
  } catch {
    // La medición nunca debe romper la interfaz.
  }
};

/** Acción en la página actual (CTA, alta, entradas, calendario, compartir). */
export const countAction = (action: AudienceAction) => {
  const v = current;
  if (!v) return;
  post("record_page_action", {
    p_path: v.path,
    p_page_type: v.pageType,
    p_city: v.city,
    p_action: action,
    p_source: v.source,
    p_device: v.device,
  });
};

// Pasar a segundo plano cierra el tiempo de la vista: en móvil es lo único que
// llega siempre. Si vuelve, esa vista ya no suma más tiempo.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushTime();
  });
}
